import { createFileRoute } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Stack } from '@mui/material';
import {
  EmailLoginFieldValues,
  EmailLoginSchema,
} from '@/constants/ValidationSchema.ts';
import EmailField from '@/components/EmailField.tsx';
import PasswordField from '@/components/PasswordField.tsx';
import useLoginManager from '@/hooks/useLoginManager.ts';
import Link from '@/components/Link.tsx';
import PageTitle from '@/components/PageTitle.tsx';

export const Route = createFileRoute('/_guest/login')({
  component: Login,
});

function Login() {
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<EmailLoginFieldValues>({
    resolver: zodResolver(EmailLoginSchema),
    mode: 'all',
  });
  const { t } = useTranslation(['manager']);
  const [loginManager, { loading }] = useLoginManager();

  const handleOnValid = useCallback(
    async ({ email, password }: EmailLoginFieldValues) => {
      await loginManager({
        email,
        password,
      });
    },
    [loginManager],
  );

  return (
    <Stack spacing={4}>
      <Stack spacing={1} alignItems="center">
        <PageTitle variant="h4" textAlign="center">
          {t('login.header')}
        </PageTitle>
      </Stack>
      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit(handleOnValid)}>
        <Controller<EmailLoginFieldValues, 'email'>
          name="email"
          control={control}
          render={EmailField}
        />
        <Controller<EmailLoginFieldValues, 'password'>
          name="password"
          control={control}
          render={PasswordField}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          loading={loading}
          disabled={!isValid}>
          {t('login.submit')}
        </Button>
        <Link to="/forgot-password">{t('login.forgotPasswordLink')}</Link>
        <Link to="/register">{t('login.registerLink')}</Link>
      </Stack>
    </Stack>
  );
}
